// Exact full-document validation. Partial drafts must be merged before validation.
const s = require('./madarSchemaUtils');
const { KIND_NAMES, KINDS, STATUSES } = require('./madarKinds');
const SCHEMA_VERSION = 1;
const enums = s.enumeration;
const list = (items) => s.array(items, { uniqueItems: true });
const kind = enums(...KIND_NAMES);
const status = enums(...STATUSES);
const boolean = { type: 'boolean' };
const imageMimes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
const documentMimes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
const bucket = (mimes) => s.shape({ mimes: list(enums(...mimes)), max_mb: s.number(1, 25) });
const command = s.nullable(s.shape({ connection_kind: enums('splice', 'patch', 'through', 'internal'),
  pairing: enums('same_number', 'sequential'), offset: s.integer(), scope: enums('whole_cable', 'tube', 'selection'),
  occupied: enums('reject_whole', 'free_only') }));
const RULE_SCHEMAS = {
  status_transitions: s.record(list(status), status), legacy_status_map: s.record(status, s.text),
  name_templates: s.record(s.text, kind), accepted_crs: list(enums(4326, 32638, 32639, 32640, 32641, 3857)),
  cable_core_counts: list(s.integer(2, 288)), library_point_buffer_m: s.nullable(s.number(1, 500)),
  group_templates_enabled: boolean, layer_zoom: s.record(s.shape({ min_zoom: s.integer(12, 22), label_min_zoom: s.integer(17, 22) }), kind),
  map_tools: list(enums('measure', 'coordinates', 'address', 'directions', 'street_view')),
  workspace_sharing: enums('personal', 'carrier'), reference_layer_sharing: enums('personal', 'carrier'),
  import_execution: enums('all_or_nothing', 'valid_rows_only'), import_repeat: enums('reject', 'return_existing'),
  import_update_mode: enums('create_only', 'update_by_external_ref'), calculated_length_mode: enums('manual', 'gis_plus_loops'),
  declared_length_check: s.shape({ mode: enums('block', 'warn'), tolerance_m: s.number(0, 1000) }),
  branch_length_total: enums('disabled', 'per_branch'), delete_cascade_kinds: list(kind), rooms_per_pop_max: s.integer(1, 20),
  document_types: s.shape({ image: bucket(imageMimes), document: bucket(documentMimes) }), photo_max_distance_m: s.number(1, 500),
  photo_failure_policy: enums('block', 'warn'), photo_linear_metric: enums('disabled', 'nearest_point'),
  photo_ocr_mode: enums('off', 'when_exif_missing'), photo_override_roles: list(enums('super_admin', 'admin')),
  pop_assignable_kinds: list(kind), pop_candidate_radius_m: s.nullable(s.number(1, 50000)),
  connected_olt_mode: enums('explicit', 'derived_proposal'), connected_olt_mismatch: enums('warn', 'block'),
  connection_kinds: list(enums('splice', 'patch', 'through', 'internal')),
  group_commands: s.shape({ splice_all: command, cross_connection: command, multiple_connection: command }),
  splice_requires_tray: boolean, tray_config_source: enums('spec_template', 'manual_within_capacity', 'manual'),
  tray_move_mode: enums('disabled', 'metadata_move', 'disconnect_reconnect'), endpoint_state_edit: boolean,
  connect_reserved: enums('block', 'allow'), connect_faulty: enums('block', 'allow'),
  fault_on_connected: enums('keep_connection', 'require_disconnect'), trace_through_faulty: enums('annotate', 'stop'),
  utilization_denominator: enums('disabled', 'physical_ports', 'sides'), duct_channel_identity: enums('number', 'number_and_color'),
  duct_channel_max_cables: s.nullable(s.integer(1, 12)), attached_end_policy: enums('block', 'follow'),
  route_move_pe_policy: enums('block', 'scale'), command_route_choice: enums('require_explicit', 'show_alternatives'),
  loop_candidate_radius_m: s.number(1, 100), loop_default_length_m: s.record(s.number(0, 500), enums(...KIND_NAMES.filter((k) => KINDS[k].geom === 'Point' || KINDS[k].geom === null))),
  bulk_area_max_km2: s.number(0.01, 50),
  optical_loss: s.nullable(s.shape({ fibre_db_per_km: s.shape(Object.fromEntries(['1270', '1310', '1490', '1550', '1577'].map((v) => [v, s.number(0)]))),
    splice_db: s.number(0), connector_db: s.number(0), splitter_db: s.record(s.number(0), { type: 'string', pattern: '^(1|2):([2-9]|[1-5][0-9]|6[0-4])$' }) })),
  pon_class: s.nullable(s.shape({ name: s.text, min_db: s.number(0), max_db: s.number(0), margin_db: s.number(0) })),
  bom_length_basis: enums('disabled', 'gis', 'calculated'), design_import_spec_map: s.record(s.uuid, { type: 'string', pattern:
    '^(pop|olt|odc|mfat_l2|fat|hh|tconnector|hconnector|trench|microduct_(metro|odc|fat|mfat_feed)|splitter_1x([2-9]|[1-5][0-9]|6[0-4])|patch_panel_([1-9][0-9]{0,2})|cable_([1-9][0-9]{0,2}))$' }),
  design_import_cables: enums('none', 'resolved_ends'), design_import_allow_manual_edited: boolean, design_import_lineage_policy: enums('block'),
};

const DEFAULTS = s.freeze({
  "status_transitions": {},
  "legacy_status_map": {},
  "name_templates": {
    "trench": "{city} - Tranch - {layer} Layer",
    "microduct": "{city} - MD - {layer} Layer",
    "cable": "{end_a} - {end_b}"
  },
  "accepted_crs": [
    4326
  ],
  "cable_core_counts": [
    12,
    24,
    48,
    72,
    96
  ],
  "library_point_buffer_m": null,
  "group_templates_enabled": false,
  "layer_zoom": {},
  "map_tools": [
    "measure",
    "coordinates"
  ],
  "workspace_sharing": "personal",
  "reference_layer_sharing": "personal",
  "import_execution": "all_or_nothing",
  "import_repeat": "return_existing",
  "import_update_mode": "create_only",
  "calculated_length_mode": "manual",
  "declared_length_check": {
    "mode": "block",
    "tolerance_m": 0
  },
  "branch_length_total": "disabled",
  "delete_cascade_kinds": [],
  "rooms_per_pop_max": 1,
  "document_types": {
    "image": {
      "mimes": [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/bmp"
      ],
      "max_mb": 10
    },
    "document": {
      "mimes": [],
      "max_mb": 10
    }
  },
  "photo_max_distance_m": 15,
  "photo_failure_policy": "block",
  "photo_linear_metric": "disabled",
  "photo_ocr_mode": "off",
  "photo_override_roles": [],
  "pop_assignable_kinds": [
    "fat"
  ],
  "pop_candidate_radius_m": null,
  "connected_olt_mode": "explicit",
  "connected_olt_mismatch": "warn",
  "connection_kinds": [
    "splice",
    "patch"
  ],
  "group_commands": {
    "splice_all": null,
    "cross_connection": null,
    "multiple_connection": null
  },
  "splice_requires_tray": true,
  "tray_config_source": "spec_template",
  "tray_move_mode": "disabled",
  "endpoint_state_edit": false,
  "connect_reserved": "block",
  "connect_faulty": "block",
  "fault_on_connected": "keep_connection",
  "trace_through_faulty": "annotate",
  "utilization_denominator": "disabled",
  "duct_channel_identity": "number_and_color",
  "duct_channel_max_cables": null,
  "attached_end_policy": "block",
  "route_move_pe_policy": "block",
  "command_route_choice": "show_alternatives",
  "loop_candidate_radius_m": 5,
  "loop_default_length_m": {},
  "bulk_area_max_km2": 3,
  "optical_loss": null,
  "pon_class": null,
  "bom_length_basis": "disabled",
  "design_import_spec_map": {},
  "design_import_cables": "none",
  "design_import_allow_manual_edited": false,
  "design_import_lineage_policy": "block"
});
// Metadata is kept with the schema for the rule editor and release coverage.
const METADATA = [
  ["status_transitions", "map", ["F19"], "none", "تغییر وضعیت", "object: status P, I or O -> array of P, I, O", []],
  ["legacy_status_map", "map", ["F08", "F10", "F19"], "none", "نگاشت وضعیت قدیمی", "object: source text -> P, I or O", []],
  ["name_templates", "map", ["F07", "F08", "F09", "F10", "F11", "F12", "F13", "F14", "F15", "F16", "F37"], "none", "الگوهای نام", "object: kind -> template with `{city}`, `{city_abbr}`, `{layer}`, `{end_a}`, `{end_b}`", []],
  ["accepted_crs", "map", ["F08", "F09", "F10", "F11", "F12", "F13", "F14", "F15", "F16", "F37"], "none", "سامانه‌های مختصات", "array of EPSG ints from 4326, 32638, 32639, 32640, 32641, 3857", []],
  ["cable_core_counts", "map", ["F07", "F16"], "none", "ظرفیت رشته کابل", "array of ints 2..288", []],
  ["library_point_buffer_m", "map", ["F07"], "none", "شعاع اتصال سر خط", "number 1..500 or null", []],
  ["group_templates_enabled", "map", ["F07"], "none", "الگوهای گروهی", "boolean", []],
  ["layer_zoom", "map", ["F02"], "none", "بزرگ‌نمایی لایه", "object: kind -> `{min_zoom 12..22, label_min_zoom 17..22}`", []],
  ["map_tools", "map", ["F04"], "none", "ابزارهای نقشه", "array from `measure`, `coordinates`, `address`, `directions`, `street_view`", []],
  ["workspace_sharing", "map", ["F06"], "none", "اشتراک فضای کار", "`personal` or `carrier`", []],
  ["reference_layer_sharing", "map", ["F09"], "none", "اشتراک لایه مرجع", "`personal` or `carrier`", []],
  ["import_execution", "imports", ["F08", "F37"], "none", "اجرای ورودی", "`all_or_nothing` or `valid_rows_only`", []],
  ["import_repeat", "imports", ["F08", "F37"], "none", "تکرار ورودی", "`reject` or `return_existing`", []],
  ["import_update_mode", "imports", ["F08"], "none", "به‌روزرسانی ورودی", "`create_only` or `update_by_external_ref`", []],
  ["calculated_length_mode", "assets", ["F16", "F17", "F31", "F34"], "Q7", "محاسبه طول", "`manual` or `gis_plus_loops`", []],
  ["declared_length_check", "assets", ["F16", "F35", "F37"], "related Q7", "کنترل طول اعلام‌شده", "object `{mode: block or warn, tolerance_m 0..1000}`", []],
  ["branch_length_total", "assets", ["F31", "F34"], "related Q7", "جمع طول شاخه", "`disabled` or `per_branch`", []],
  ["delete_cascade_kinds", "assets", ["F20"], "Q6", "حذف وابستگان", "array of kinds", []],
  ["rooms_per_pop_max", "assets", ["F32"], "none", "حد اتاق سایت", "int 1..20", []],
  ["document_types", "documents", ["F21"], "none", "نوع پرونده", "object `{image:{mimes, max_mb}, document:{mimes, max_mb}}`, mimes within the bucket list, max_mb 1..25", []],
  ["photo_max_distance_m", "documents", ["F36"], "none", "فاصله عکس", "number 1..500", []],
  ["photo_failure_policy", "documents", ["F36"], "none", "خطای عکس", "`block` or `warn`", []],
  ["photo_linear_metric", "documents", ["F36"], "none", "معیار عکس خطی", "`disabled` or `nearest_point`", []],
  ["photo_ocr_mode", "documents", ["F36"], "none", "خواندن مختصات عکس", "`off` or `when_exif_missing`", []],
  ["photo_override_roles", "documents", ["F36"], "none", "مجوز استثنای عکس", "array from `super_admin`, `admin`", []],
  ["pop_assignable_kinds", "connectivity", ["F22"], "none", "انتساب سایت", "array of kinds", []],
  ["pop_candidate_radius_m", "connectivity", ["F22"], "none", "شعاع سایت پیشنهادی", "number 1..50000 or null", []],
  ["connected_olt_mode", "connectivity", ["F22"], "Q8", "انتخاب OLT", "`explicit` or `derived_proposal`", []],
  ["connected_olt_mismatch", "connectivity", ["F22"], "Q8", "تعارض OLT", "`warn` or `block`", []],
  ["connection_kinds", "connectivity", ["F26", "F28", "F29", "F33", "F38"], "Q1", "نوع اتصال", "array from `splice`, `patch`, `through`, `internal`", []],
  ["group_commands", "connectivity", ["F29", "F33", "F38"], "Q2", "دستورهای گروهی", "object with keys `splice_all`, `cross_connection`, `multiple_connection`, each null or a definition (3.3)", []],
  ["splice_requires_tray", "connectivity", ["F24", "F26"], "Q4", "نیاز به کاست", "boolean", []],
  ["tray_config_source", "connectivity", ["F24"], "Q4", "منبع کاست", "`spec_template`, `manual_within_capacity`, `manual`", []],
  ["tray_move_mode", "connectivity", ["F24", "F27"], "Q4", "انتقال کاست", "`disabled`, `metadata_move`, `disconnect_reconnect`", []],
  ["endpoint_state_edit", "connectivity", ["F28"], "Q5", "ویرایش سر", "boolean", []],
  ["connect_reserved", "connectivity", ["F25", "F26", "F28"], "Q5", "سر رزرو", "`block` or `allow`", []],
  ["connect_faulty", "connectivity", ["F25", "F26", "F28"], "Q5", "سر معیوب", "`block` or `allow`", []],
  ["fault_on_connected", "connectivity", ["F27", "F28"], "Q5", "خرابی سر متصل", "`keep_connection` or `require_disconnect`", []],
  ["trace_through_faulty", "connectivity", ["F28", "F31"], "Q5", "ردیابی خرابی", "`annotate` or `stop`", []],
  ["utilization_denominator", "connectivity", ["F34"], "Q3, Q5", "مبنای بهره‌برداری", "`disabled`, `physical_ports`, `sides`", []],
  ["duct_channel_identity", "civil", ["F11", "F35", "F37"], "Q10", "شناسه زیرمجرا", "`number` or `number_and_color`", []],
  ["duct_channel_max_cables", "civil", ["F11", "F35", "F37"], "Q10", "ظرفیت زیرمجرا", "int 1..12 or null", []],
  ["attached_end_policy", "civil", ["F18", "F35"], "Q10", "حرکت سر متصل", "`block` or `follow`", []],
  ["route_move_pe_policy", "civil", ["F35"], "related Q10", "حرکت غلاف", "`block` or `scale`", []],
  ["command_route_choice", "civil", ["F37"], "related Q10", "انتخاب مسیر دستور", "`require_explicit` or `show_alternatives`", []],
  ["loop_candidate_radius_m", "civil", ["F17"], "none", "شعاع لوپ", "number 1..100", []],
  ["loop_default_length_m", "civil", ["F17"], "related Q7", "طول پیش‌فرض لوپ", "object: location kind -> number 0..500", []],
  ["bulk_area_max_km2", "civil", ["F19", "F20"], "none", "مساحت انتخاب", "number 0.01..50", []],
  ["optical_loss", "reports", ["F34"], "none", "افت نوری", "null or `{fibre_db_per_km:{\"1270\",\"1310\",\"1490\",\"1550\",\"1577\": number}, splice_db, connector_db (per mated interface), splitter_db:{\"<inputs>:<outputs>\": number}}`", []],
  ["pon_class", "reports", ["F34"], "none", "کلاس PON", "null or `{name, min_db, max_db, margin_db}`", []],
  ["bom_length_basis", "reports", ["F34"], "Q7", "طول صورت مصالح", "`disabled`, `gis`, `calculated`", []],
  ["design_import_spec_map", "design", [], "none", "مدل ورودی طراحی", "object: source key -> spec id of this carrier; keys `pop`, `olt`, `odc`, `mfat_l2`, `fat`, `hh`, `tconnector`, `hconnector`, `trench`, `microduct_metro`, `microduct_odc`, `microduct_fat`, `microduct_mfat_feed`, `splitter_1x<n>`, `patch_panel_<cores>`, `cable_<cores>`", ["specs"]],
  ["design_import_cables", "design", [], "none", "کابل ورودی طراحی", "`none` or `resolved_ends`", []],
  ["design_import_allow_manual_edited", "design", [], "none", "طراحی ویرایش‌شده", "boolean", []],
  ["design_import_lineage_policy", "design", [], "none", "تبار طراحی", "`block` (the only v1 value; other values reserved for a later supersede workflow)", []],
];
const SETTINGS = s.freeze(METADATA.map(([key, group, f_codes, question, label_fa, allowed, catalogue_refs]) => ({
  key, type: RULE_SCHEMAS[key].type || (RULE_SCHEMAS[key].anyOf ? "nullable" : "enum"), allowed: RULE_SCHEMAS[key],
  default: DEFAULTS[key], group, f_codes, question, label_fa, help_fa: `${label_fa}؛ مقدار فقط مطابق قواعد معتبر پذیرفته می‌شود.`,
  catalogue_refs, contract: allowed,
})));
function designTarget(key) {
  const fixed = { pop: 'pop', olt: 'olt', odc: 'odc', mfat_l2: 'mfat', fat: 'fat', hh: 'handhole',
    tconnector: 't_connector', hconnector: 'h_connector', trench: 'trench' };
  if (s.own(fixed, key)) return { kind: fixed[key] };
  const match = /^(cable|splitter_1x|patch_panel|microduct)_(.*)$/.exec(key);
  if (key.startsWith('splitter_1x')) return { kind: 'splitter', inputs: 1, outputs: Number(key.slice(11)) };
  if (!match) return null;
  if (match[1] === 'cable') return { kind: 'cable', core_count: Number(match[2]) };
  if (match[1] === 'patch_panel') return { kind: 'patch_panel', port_count: Number(match[2]) };
  return { kind: 'microduct', channel_class: ['metro', 'odc'].includes(match[2]) ? '7_way' : '4_way' };
}
// specsById must be destination-carrier scoped. carrierId additionally verifies that scope.
function validateRules(input, { specsById, carrierId } = {}) {
  const errors = s.check(s.shape(RULE_SCHEMAS), input);
  if (errors.length) return s.result(input, errors);
  const fail = (field, reason, code = 'INVALID_VALUE') => errors.push({ code, field, reason });
  Object.entries(input.name_templates).forEach(([key, template]) => {
    const remaining = template.replace(/\{(city|city_abbr|layer|end_a|end_b)\}/g, '');
    if (/[{}]/.test(remaining)) fail(`name_templates.${key}`, 'placeholder');
  });
  Object.entries(input.layer_zoom).forEach(([key, value]) => {
    if (value.label_min_zoom < value.min_zoom) fail(`layer_zoom.${key}`, 'label_before_layer');
  });
  Object.entries(input.group_commands).forEach(([key, value]) => {
    if (value && !input.connection_kinds.includes(value.connection_kind)) fail(`group_commands.${key}.connection_kind`, 'disabled_kind');
  });
  if (input.pon_class && (input.pon_class.min_db > input.pon_class.max_db
    || input.pon_class.margin_db > input.pon_class.max_db - input.pon_class.min_db)) fail('pon_class', 'invalid_budget');
  Object.entries(input.design_import_spec_map).forEach(([key, id]) => {
    const target = designTarget(key);
    if (!target || (target.kind === 'cable' && (target.core_count < 2 || target.core_count > 288))
      || (target.kind === 'patch_panel' && target.port_count > 576)) { fail(`design_import_spec_map.${key}`, 'capacity'); return; }
    if (!specsById) return;
    const spec = specsById instanceof Map ? specsById.get(id) : (s.own(specsById, id) ? specsById[id] : null);
    if (!spec || spec.status !== 'active' || spec.kind !== target.kind || (carrierId && spec.carrier_id !== carrierId)
      || Object.entries(target).some(([attr, value]) => attr !== 'kind' && spec.attrs?.[attr] !== value)) {
      fail(`design_import_spec_map.${key}`, 'inactive_foreign_or_capacity', 'SPEC_MISMATCH');
    }
  });
  return s.result(Object.fromEntries(SETTINGS.map(({ key }) => [key, input[key]])), errors);
}
module.exports = s.freeze({ SCHEMA_VERSION, SETTINGS, DEFAULTS, RULE_SCHEMAS, validateRules, designTarget });
